import { translate } from 'helpers/translateHelper';
import { assetCondition } from './constants';

export const priorityCondition = {
    [assetCondition.CONDITION_ALARM]: 1,
    [assetCondition.CONDITION_POOR]: 1,
    [assetCondition.CONDITION_ALERT]: 2,
    [assetCondition.CONDITION_TOLERABLE]: 2
};

export const modifiedDriveEvents = (data) => {
    return {
        protection: {
            fault: data.protectionFaultsCount,
            warning: data.protectionWarningsCount
        },
        operational: {
            fault: data.operationalFaultsCount,
            warning: data.operationalWarningsCount
        },
        other: {
            fault: data.otherFaultsCount,
            warning: data.otherWarningsCount
        }
    };
};

export const getTranslateSmartConditionTitle = (signal) => {
    if (signal?.titleKey) {
        return translate(signal?.titleKey) || signal?.title || '';
    }
    return signal?.title || '';
};

export const getTranslateSmartConditionTexts = (signal) => {
    if (signal?.textKey) {
        return translate(signal?.textKey) || signal?.text || '';
    }
    return signal?.text || '';
};

/**
 * Parses recommendation text and formats it as a numbered or bulleted list
 * @param {string} text - The recommendation text
 * @returns {object} - Object with isFormatted flag, prefix, items array, and suffix
 */
export const parseRecommendationText = (text) => {
    if (!text) {
        return { isFormatted: false, prefix: '', items: [], suffix: '' };
    }

    // Pattern to match parentheses numbered format: "(1)", "(2)", "(3)", etc.
    const parenthesesPattern = /\((\d+)\)\s+/g;
    const hasParenthesesNumbers = text?.match(parenthesesPattern);

    // Check if text contains parentheses numbered items like (1), (2), (3)
    if (hasParenthesesNumbers && hasParenthesesNumbers?.length > 1) {
        const items = [];
        const positions = [];
        let match;
        const regex = /\((\d+)\)\s+/g;

        while ((match = regex.exec(text)) !== null) {
            positions.push({
                index: match?.index,
                number: match[1],
                endOfMatch: match?.index + match[0]?.length
            });
        }

        // Extract prefix (text before first numbered item)
        const prefix = text?.substring(0, positions[0].index).trim();

        // First, extract suffix (text after last numbered item)
        // Look for sentences that start with capital letter after a period
        const afterLastItem = text?.substring(positions[positions.length - 1].endOfMatch);
        const suffixMatch = afterLastItem?.match(/\.\s+([A-Z][^]*?)$/);
        const suffix = suffixMatch ? suffixMatch[1].trim() : '';

        // Extract content between positions
        for (let i = 0; i < positions?.length; i++) {
            const start = positions[i].endOfMatch;
            const end = i < positions?.length - 1 ? positions[i + 1].index : text?.length;
            let content = text?.substring(start, end).trim();

            // For the last item, remove the suffix if it exists
            if (i === positions?.length - 1 && suffix) {
                const suffixStartIndex = content?.lastIndexOf('. ' + suffix);
                if (suffixStartIndex !== -1) {
                    content = content?.substring(0, suffixStartIndex + 1).trim();
                }
            }

            // Clean up the content
            // Remove trailing semicolons and "and/or"
            content = content
                .replace(/;\s*and\/or\s*$/, '')
                .replace(/;\s*$/, '')
                .trim();

            if (content) {
                items?.push(content);
            }
        }

        if (items?.length > 0) {
            return { isFormatted: true, prefix, items, suffix };
        }
    }

    // Pattern to match numbered lists: "1.", "2.", etc. at the start of a line or after semicolon
    const numberedPattern = /(?:^|\s|;)\s*(\d+)\.\s+/;

    // Check if text contains numbered list items
    if (numberedPattern?.test(text)) {
        // Split by numbered pattern and filter empty strings
        const parts = text?.split(/(?:^|;|\n)\s*\d+\.\s+/);
        const prefix = parts[0]?.trim() || '';
        const items = parts?.slice(1).map(item => item?.trim()).filter(item => item?.length > 0);

        return { isFormatted: true, prefix, items, suffix: '' };
    }

    // Pattern to match bullet points or line breaks with content
    const bulletPattern = /(?:^|\n)\s*[-•]\s+/;

    if (bulletPattern?.test(text)) {
        const parts = text?.split(/(?:^|\n)\s*[-•]\s+/);
        const prefix = parts[0]?.trim() || '';
        const items = parts?.slice(1).map(item => item?.trim()).filter(item => item?.length > 0);

        return { isFormatted: true, prefix, items, suffix: '' };
    }

    // If no formatting detected, return as single item
    return { isFormatted: false, prefix: '', items: [], suffix: text };
};
